import { useLocation } from "react-router-dom";
import { useTooltipGroup, useTooltipGroups } from "./useTooltipGroup";

/**
 * Resolve which admin-authored tooltip group belongs to the current route.
 * Longest matching `route_prefix` wins, so `/clip-studio/compose` can have a
 * more specific group than `/clip-studio`.
 */
export function useRouteTooltipGroupKey(): string | null {
  const { pathname } = useLocation();
  const { data: groups = [] } = useTooltipGroups();

  const match = groups
    .filter((g) => g.is_enabled && g.route_prefix?.trim())
    .filter((g) => {
      const prefix = g.route_prefix!.trim().replace(/\/+$/, "") || "/";
      return prefix === "/" ? true : pathname === prefix || pathname.startsWith(`${prefix}/`);
    })
    .sort((a, b) => (b.route_prefix?.length ?? 0) - (a.route_prefix?.length ?? 0))[0];

  return match?.key ?? null;
}

/**
 * Availability of tooltip surfaces for the current route, honoring the admin
 * ceiling (group enabled + per-surface flags + at least one enabled tip).
 * Used by the profile menu and the tour launcher so they never disagree.
 */
export function useRouteTooltipAvailability() {
  const groupKey = useRouteTooltipGroupKey();
  const { data } = useTooltipGroup(groupKey ?? "");

  const groupEnabled = !!data?.group.is_enabled;
  const hasTips = groupEnabled && data.tips.length > 0;
  return {
    groupKey,
    groupEnabled,
    tipsAvailable: hasTips && !!data?.group.tips_enabled,
    tourAvailable: hasTips && !!data?.group.tour_enabled,
  };
}
